import React, { useState, useEffect, useRef } from "react";
import { Input, Checkbox, Button, Spin } from "antd";
import { SearchOutlined, DownOutlined } from "@ant-design/icons";
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../CustomWidgets/Laoding";
import { detectWebDeviceType } from "../../utils";
import { store } from "../../Redux/store";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";


const searchProductStockAvailability = async (
    val,
    searchBy,
    userId,
    companyId,
    limit,
    offset,
    lang
) => {
    try {
        const shared = {
            user_id: userId,
            company_id: companyId,
            limit,
            offset,
            lang,
        };

        let payload = {};

        if (searchBy === "BARCODE") {
            payload = {
                ...shared,
                barcode: val,
            };
        } else if (searchBy === "NAME") {
            payload = {
                ...shared,
                product_name: val,
            };
        } else {
            payload = {
                ...shared,
                short_code: val,
            };
        }

        const res = await axiosInstance.post(
            process.env.REACT_APP_API_URL_OLD + URLS.GET_AVAILABLE_STOCK_INFO,
            payload
        );

        console.log(res?.result?.response)
        return res;
    } catch (error) {
        console.error("Stock availability error:", error);
        throw error;
    }
};

function CheckStockAvailabilityModal({ open, onClose }) {
    const { t, i18n } = useTranslation();

    const [searchValue, setSearchValue] = useState("");
    const [shortCodeSearch, setShortCodeSearch] = useState("")
    const [searchBy, setSearchBy] = useState("NAME");
    const [stockData, setStockData] = useState([]);
    const userData = useSelector((state) => state?.login?.user?.result);
    const deviceType = detectWebDeviceType();
    const [showLeft, setShowLeft] = useState(false);
    const [storeStock, setStoreStock] = useState([])
    const [showStoreStockTable, setShowStoreStockTable] = useState(false)
    const isRTL = i18n.language === "ar";



    const [loading, setLoading] = useState(false);

    const userId = userData?.user_id;
    const companyId = userData?.company_id;
    const lang = "en_US";

    const limit = 10;
    const offset = 0;

    const searchIdRef = useRef(0);

    // Lock body scroll while the modal is open, same as the generic Modal component
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [open]);

    const handleSearch = async () => {
        const valueToSearch =
            searchBy === "SHORT_CODE"
                ? shortCodeSearch
                : searchValue;

        // Invalidate any in-flight request every time handleSearch runs
        const requestId = ++searchIdRef.current;

        if (!valueToSearch.trim()) {
            setStockData([]);
            setShowStoreStockTable(false);
            setStoreStock([]);
            return;
        }

        try {
            setLoading(true);

            const response = await searchProductStockAvailability(
                valueToSearch.trim(),
                searchBy,
                userId,
                companyId,
                limit,
                offset,
                lang
            );

            // If a newer search has started since this one was fired, ignore this stale response
            if (requestId !== searchIdRef.current) return;

            setStockData(response?.data?.result?.[0]?.response || []);
        } catch (error) {
            if (requestId !== searchIdRef.current) return;
            setStockData([]);
        } finally {
            if (requestId === searchIdRef.current) {
                setLoading(false);
            }
        }
    };


    const handleClose = () => {
        setSearchValue("")
        setSearchBy("NAME")
        setStockData([])
        onClose?.();
    };



    // Optional: search automatically when the search type changes
    useEffect(() => {
        const valueToSearch =
            searchBy === "SHORT_CODE"
                ? shortCodeSearch
                : searchValue;

        const timer = setTimeout(() => {
            if (valueToSearch.length === 0 || valueToSearch.length > 2) {
                handleSearch();
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchValue, shortCodeSearch, searchBy]);

    if (!open) {
        return null;
    }

    return (
        <div
            className={
                deviceType === "pc"
                    ? "fixed inset-0 bg-black bg-opacity-50 backdrop-blur flex items-center justify-center p-4 z-50"
                    : ""
            }
            style={
                deviceType === "tablet"
                    ? {
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        zIndex: 9999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 16,
                    }
                    : {}
            }
        >

            <div
                className={`bg-white rounded-2xl ${deviceType === "tablet" ? "w-[55%] fixed left-16 " : "w-[80%]"
                    }`}
            >
                <div className="flex justify-between items-center p-6 border rounded-lg">
                    <h2 className="text-2xl font-semibold">{t("Stock Availability Info")}</h2>
                    <button type="button" onClick={handleClose} className="bg-transparent">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-6 h-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div className="p-6">
                    <div className="flex gap-6 items-start min-w-0">

                        <div className="w-3/5 min-w-0 flex-shrink-0">

                            <div className="mb-6">
                                <div className="flex mb-3 rounded-xl min-w-0">
                                    <Input
                                        size="large"
                                        prefix={<SearchOutlined className="text-xl mr-3" />}
                                        placeholder={searchBy==="SHORT_CODE"? t("Search By Short Code"):t("Search Products Name/Barcode")}
                                        value={searchBy === "SHORT_CODE" ? shortCodeSearch : searchValue}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(
                                                /[^\u0600-\u06FFa-zA-Z0-9\s]/g,
                                                ""
                                            );

                                            if (searchBy === "SHORT_CODE") {
                                                setShortCodeSearch(val.trimStart());
                                            } else {
                                                setSearchValue(val.trimStart());
                                                setSearchBy("NAME");
                                            }
                                        }}
                                        maxLength={25}
                                        className="text-lg h-14 rounded-lg min-w-0 flex-1"
                                       
                                    />
                                
                                            
                                            <div style={{ padding: 5,
                                                border: "none",
                                                background: "transparent",}}/>
                                                <button
                                                type="button"
                                                onClick={() => {
                                                    if (searchBy === "SHORT_CODE") {
                                                        setSearchBy("NAME");
                                                        setShortCodeSearch("");
                                                    } else {
                                                        setSearchBy("SHORT_CODE");
                                                        setSearchValue("");
                                                    }
                                                }}
                                                className={`h-14 w-[150px] flex-shrink-0 border text-lg whitespace-nowrap rounded-md 
  ${searchBy === "SHORT_CODE"
                                                        ? "bg-blue-100 border-[#1b4597] text-[#1b4597]"
                                                        : "bg-white border-gray-300 text-gray-700"
                                                    }`}
                                            >
                                                {t("Search Code")}
                                            </button>
                                        

                                </div>
                            </div>

                            <div className="w-full">
                                {loading ? (
                                    <div className="flex justify-center py-4">
                                        <div className="w-8 h-8 border-2 border-gray-200 border-t-[#8DC63F] rounded-full animate-spin" />
                                    </div>
                                ) : stockData.length > 0 ? (
                                    <div className="w-full h-[400px] border rounded-lg bg-white p-3 overflow-y-auto">
                                        {stockData.map((stock) => (
                                            <div
                                                key={stock.id}
                                                className={`flex items-center py-2 px-2 rounded-md cursor-pointer hover:bg-green-50 ${storeStock === stock.store_wise_quantity &&
                                                        showStoreStockTable
                                                        ? "border border-[#8DC63F] bg-[#f6fbf0]"
                                                        : ""
                                                    }`}
                                                onClick={() => {
                                                    setShowStoreStockTable(true);
                                                    setStoreStock(stock.store_wise_quantity);
                                                }}
                                            >
                                                <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded bg-white border border-gray-100 overflow-hidden">
                                                    <img
                                                        src={stock.image_url}
                                                        alt={stock.name}
                                                        className="w-full h-full object-contain"
                                                    />
                                                </div>

                                                <div className="flex-1 ml-3">
                                                    <h3 className="font-medium text-gray-800">
                                                        {stock.name}
                                                    </h3>

                                                    <p className="text-sm text-gray-500">
                                                        Barcode: {stock.barcode}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : searchValue || shortCodeSearch ? (
                                    <div className="flex flex-col items-center justify-center text-lg h-[400px] py-6 text-gray-500">
                                        <p className="text-md">{t("No products found")}</p>
                                    </div>
                                ) : (
                                    <div className="flex  items-center justify-center py-6 text-gray-400 h-[400px]">
                                        <SearchOutlined className="text-2xl mr-2" />
                                        <p className="text-lg mt-1">
                                            {t("Search by product name, barcode or short code")}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="w-2/5 min-w-0 flex-shrink-0">
                            <div className="w-full h-[500px] flex flex-col bg-gray-50 p-4 rounded-lg overflow-hidden">
                                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                                    <h3 className="text-xl font-semibold">{t("Store Stock")}</h3>
                                </div>
                                {showStoreStockTable ? (
                                    storeStock?.length > 0 ? (
                                        <>
                                            <div className="grid grid-cols-2 font-semibold text-lg  text-gray-600 pb-2 sticky top-0 bg-gray-50 flex-shrink-0">
                                                <span>{t("Store Name")}</span>
                                                <span className="text-right">{t("Quantity")}</span>
                                            </div>
                                            <div className="space-y-2 flex-1 overflow-y-auto pe-2 scrollbar-thin">
                                                {storeStock.map((store, index) => (
                                                    <div
                                                        key={`${store.store_name}-${index}`}
                                                        className="grid grid-cols-2 text-md text-gray-800 py-2 border-b border-gray-200 hover:bg-gray-100 transition-colors"
                                                    >
                                                        <span>{store.store_name}</span>
                                                        <span
                                                            className={`text-right font-medium ${Number(store.qty) < 0
                                                                ? "text-red-900"
                                                                : "text-gray-900"
                                                                }`}
                                                        >
                                                            {store.qty}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-gray-600 py-2 text-center">
                                            {t("No store stock available")}
                                        </div>
                                    )
                                ) : (
                                    <div className="h-full flex items-center justify-center text-lg text-gray-600 py-2 text-center">
                                        {t("Select a product to view store-wise stock")}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default CheckStockAvailabilityModal;